import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import MuiDialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { css } from '@emotion/core';

import Button from 'features/common/Mui/Button';
import ButtonWithLoadingEffect from 'features/common/ButtonWithLoadingEffect';
import { neutral700, neutral900 } from '@fever/ui';
import { setTextVariantStyle } from 'utils/text';

const useStyles = makeStyles(theme => ({
    root: {
        '& .MuiBackdrop-root': {
            display: 'none'
        }
    },
    title: {
        padding: theme.spacing(2)
    },
    subtitle: {
        color: neutral700
    },
    content: {
        padding: props =>
            props.disableContentPadding ? '0' : `${theme.spacing(3)}px ${theme.spacing(2)}px`,
        boxSizing: 'border-box'
    },
    paperScrollPaper: {
        maxHeight: 'initial',
        borderRadius: 12,
        display: props => (props.hide ? 'none' : 'flex')
    },
    paperScrollBody: {
        borderRadius: 12
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(2),
        top: theme.spacing(2),
        color: neutral900
    },
    button: {
        minWidth: '90px'
    }
}));

function Dialog({
    ariaLabelledBy = 'simple-dialog-title',
    ariaDescribedBy = 'simple-dialog-description',
    testId = 'mui-dialog',
    disableBackdropClick = false,
    disableEscapeKeyDown = false,
    disableStartButton,
    disableEndButton,
    disableLeftButton,
    disableButtons,
    disableCloseButton,
    disableContentPadding,
    disableBackdrop,
    showClose = false,
    title,
    subTitle,
    onClose = () => {},
    onCancel = () => {},
    onSuccess = () => {},
    open,
    hide,
    startButton = true,
    startButtonText,
    startButtonStyle,
    startButtonVariant = 'contained',
    endButton = true,
    endButtonVariant = 'contained',
    endButtonText,
    endButtonStyle,
    endButtonLoading,
    children,
    color,
    className,
    maxWidth,
    scroll = 'body',
    dialogContentStyle,
    dialogStyle,
    leftButton = false,
    leftButtonText,
    leftButtonStyle,
    leftButtonIcon,
    leftButtonColor,
    onLeftButtonClick = () => {}
}) {
    const classes = useStyles({ hide, disableContentPadding });

    const handleClose = (event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return false;
        }

        if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
            return false;
        }

        if (typeof onClose === 'function') {
            onClose();
        }
    };

    function handleSuccess() {
        onSuccess();
    }

    const divActionWrapper = (children, hasLeftButton) =>
        hasLeftButton ? <div css={css(divActionWrapperStyle)}>{children}</div> : children;

    return (
        <MuiDialog
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-modal="true"
            role="dialog"
            open={open}
            onClose={handleClose}
            classes={{
                root: `${disableBackdrop && classes.root} ${className}`,
                paperScrollPaper: classes.paperScrollPaper,
                paperScrollBody: classes.paperScrollBody
            }}
            maxWidth={maxWidth}
            scroll={scroll}
            css={css(dialogStyle)}
            data-testid={testId}
        >
            {showClose ? (
                <IconButton
                    aria-label="close"
                    classes={{ root: classes.closeButton }}
                    onClick={handleClose}
                    size="small"
                    disabled={disableCloseButton}
                    data-testid="dialog-close-button"
                >
                    <CloseIcon aria-hidden="true" />
                </IconButton>
            ) : null}
            {Boolean(title) && (
                <DialogTitle
                    disableTypography
                    id={ariaLabelledBy}
                    classes={{ root: classes.title }}
                    data-testid="dialog-title"
                >
                    <Typography variant="h6" css={css(setTextVariantStyle('h6'))}>
                        {title}
                    </Typography>
                    {Boolean(subTitle) && (
                        <Typography variant="subtitle2" classes={{ root: classes.subtitle }}>
                            {subTitle}
                        </Typography>
                    )}
                </DialogTitle>
            )}
            <DialogContent
                id={ariaDescribedBy}
                classes={{ root: classes.content }}
                css={dialogContentStyle}
                data-testid="dialog-content"
            >
                {children}
            </DialogContent>
            {!disableButtons && (
                <DialogActions
                    classes={{ root: classes.title }}
                    css={css(actionWrapperStyle(leftButton))}
                >
                    {leftButton && (
                        <Button
                            color={leftButtonColor}
                            onClick={onLeftButtonClick}
                            className={classes.button}
                            css={css([leftButtonDefaultStyle, leftButtonStyle])}
                            startIcon={leftButtonIcon}
                            disabled={disableLeftButton}
                        >
                            {leftButtonText}
                        </Button>
                    )}

                    {divActionWrapper(
                        <>
                            {startButton && (
                                <Button
                                    className={classes.button}
                                    onClick={onCancel || handleClose}
                                    variant={startButtonVariant}
                                    css={startButtonStyle}
                                    disabled={disableStartButton}
                                    data-testid="dialog-start-button"
                                >
                                    {startButtonText}
                                </Button>
                            )}
                            {endButton && (
                                <ButtonWithLoadingEffect
                                    className={classes.button}
                                    variant={endButtonVariant}
                                    color={color}
                                    onClick={handleSuccess}
                                    disabled={disableEndButton}
                                    css={endButtonStyle}
                                    isLoading={endButtonLoading}
                                    data-testid="dialog-end-button"
                                >
                                    {endButtonText}
                                </ButtonWithLoadingEffect>
                            )}
                        </>,
                        leftButton
                    )}
                </DialogActions>
            )}
        </MuiDialog>
    );
}

const leftButtonDefaultStyle = css`
    padding: 0px;

    .MuiSvgIcon-root {
        font-size: 18px;
    }
`;

const actionWrapperStyle = leftButton => css`
    ${leftButton &&
    `&.MuiDialogActions-root{
        display:flex; 
        align-items:center; 
        justify-content:space-between;
    }`}
`;

const divActionWrapperStyle = css`
    display: flex;
`;

Dialog.propTypes = {
    title: PropTypes.node,
    onClose: PropTypes.func,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func,
    open: PropTypes.bool,
    hide: PropTypes.bool,
    children: PropTypes.any,
    disableBackdropClick: PropTypes.bool,
    disableEscapeKeyDown: PropTypes.bool,
    disableEndButton: PropTypes.bool,
    disableButtons: PropTypes.bool,
    disableContentPadding: PropTypes.bool,
    startButton: PropTypes.bool,
    startButtonText: PropTypes.string,
    startButtonStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    startButtonVariant: PropTypes.string,
    endButton: PropTypes.bool,
    endButtonText: PropTypes.string,
    endButtonVariant: PropTypes.string,
    endButtonStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    showClose: PropTypes.bool,
    color: PropTypes.string,
    className: PropTypes.string,
    maxWidth: PropTypes.string,
    dialogContentStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default Dialog;
